// src/components/CSVUpload.jsx

import React, { useState, useContext, useEffect, useRef, useReducer } from 'react';
import { useIntl } from 'react-intl';
import { db, auth } from '../config/firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore';
import { AuthContext } from '../context/AuthContext';
import Papa from 'papaparse'; // Import PapaParse
import ConfirmationModal from './ConfirmationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE_URL =
  import.meta.env.VITE_CSV_UPLOAD_SERVICE_URL ||
  import.meta.env.VITE_CSV_PROCESSOR_URL ||
  '';

const initialState = {
  file: null,
  result: [],
  uploading: false,
  fieldMap: null,
  isDragging: false,
  saving: false,
  currentJobId: null,
  jobStatus: null,
  showDeleteConfirmation: false,
  isDeletingAll: false,
};

function csvUploadReducer(state, action) {
  switch (action.type) {
    case 'SET_FILE':
      return { ...initialState, file: action.payload };
    case 'SET_DRAGGING':
      return { ...state, isDragging: action.payload };
    case 'PREVIEW_START':
      return { ...state, uploading: true, result: [] };
    case 'PREVIEW_SUCCESS':
      return { ...state, uploading: false, result: action.payload.properties, fieldMap: action.payload.fieldMap };
    case 'PREVIEW_ERROR':
      return { ...state, uploading: false, file: null, fieldMap: null };
    case 'SAVE_START':
      return { ...state, saving: true, currentJobId: null, jobStatus: null };
    case 'SAVE_INITIATED':
      return { ...state, currentJobId: action.payload };
    case 'SAVE_ERROR':
      return { ...state, saving: false };
    case 'JOB_UPDATE':
      return { ...state, jobStatus: action.payload };
    case 'JOB_COMPLETE':
      return { ...initialState };
    case 'DELETE_ALL_START':
      return { ...state, isDeletingAll: true };
    case 'DELETE_ALL_COMPLETE':
      return { ...initialState };
    case 'DELETE_ALL_ERROR':
      return { ...state, isDeletingAll: false };
    case 'TOGGLE_DELETE_CONFIRMATION':
      return { ...state, showDeleteConfirmation: !state.showDeleteConfirmation };
    case 'RESET':
      return { ...initialState };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

export default function CSVUpload(props) {
  const { user } = useContext(AuthContext);
  const intl = useIntl();
  const [state, dispatch] = useReducer(csvUploadReducer, initialState);
  const { onUploadSuccess, hasExistingProperties } = props;

  const {
    file,
    result,
    uploading,
    fieldMap,
    isDragging,
    saving,
    currentJobId,
    jobStatus,
    showDeleteConfirmation,
    isDeletingAll,
  } = state;

  const unsubscribeJobListenerRef = useRef(null);

  // Effect for listening to Firestore job status updates
  useEffect(() => {
    if (!user?.uid || !currentJobId) {
      return;
    }

    const jobDocRef = doc(db, `users/${user.uid}/csv_jobs/${currentJobId}`);
    unsubscribeJobListenerRef.current = onSnapshot(jobDocRef, async (docSnap) => {
      if (!docSnap.exists()) {
        toast.error(intl.formatMessage({ id: 'csvUpload.jobStatusListenError' }));
        dispatch({ type: 'JOB_COMPLETE' });
        return;
      }

      const statusData = docSnap.data();
      dispatch({ type: 'JOB_UPDATE', payload: statusData });

      const isJobFinished = ['completed', 'completed_with_errors', 'failed'].includes(statusData.status);

      if (isJobFinished) {
        // Unsubscribe from further updates to prevent multiple triggers
        if (unsubscribeJobListenerRef.current) {
          unsubscribeJobListenerRef.current();
          unsubscribeJobListenerRef.current = null;
        }

        // Display the final status toast
        if (statusData.status === 'completed') toast.success(statusData.message || intl.formatMessage({ id: 'csvUpload.jobCompletedSuccess' }));
        else if (statusData.status === 'completed_with_errors') toast.warn(statusData.message || intl.formatMessage({ id: 'csvUpload.jobCompletedWithErrors' }));
        else toast.error(statusData.message || intl.formatMessage({ id: 'csvUpload.jobFailed' }));

        // Add a delay to allow Firestore indexes to update before refreshing the UI
        setTimeout(() => {
          dispatch({ type: 'JOB_COMPLETE' });
          if (onUploadSuccess) onUploadSuccess();
        }, 2000); // 2-second delay
      }
    }, (error) => {
      console.error("Error listening to job status:", error);
      toast.error(intl.formatMessage({ id: 'csvUpload.jobStatusListenError' }));
      dispatch({ type: 'JOB_COMPLETE' });
    });

    return () => {
      if (unsubscribeJobListenerRef.current) {
        unsubscribeJobListenerRef.current();
        unsubscribeJobListenerRef.current = null;
      }
    };
  }, [user?.uid, currentJobId, intl, onUploadSuccess]);

  useEffect(() => {
    return () => {
      if (unsubscribeJobListenerRef.current) {
        unsubscribeJobListenerRef.current();
      }
    };
  }, []);

  const processFile = async (selectedFile) => {
    if (selectedFile && selectedFile.type === "text/csv") {
      dispatch({ type: 'SET_FILE', payload: selectedFile });

      try {
        const text = await selectedFile.text();
        Papa.parse(text, {
          header: true,
          preview: 1,
          complete: (results) => {
            if (results.meta.fields) {
              const newFieldMap = {};
              results.meta.fields.forEach(csvHeader => {
                newFieldMap[csvHeader] = csvHeader;
              });
              dispatch({ type: 'PREVIEW_SUCCESS', payload: { properties: [], fieldMap: newFieldMap } });
            } else {
              dispatch({ type: 'PREVIEW_SUCCESS', payload: { properties: [], fieldMap: {} } });
            }
          },
          error: (err) => {
            toast.error(intl.formatMessage({ id: "csvUpload.csvParseError" }));
            dispatch({ type: 'PREVIEW_ERROR' });
          }
        });
      } catch (error) {
        toast.error(intl.formatMessage({ id: "csvUpload.fileReadError" }));
        dispatch({ type: 'PREVIEW_ERROR' });
      }
    } else if (selectedFile) {
      toast.error(intl.formatMessage({ id: "csvUpload.invalidFileType" }));
      dispatch({ type: 'RESET' });
    }
  };

  const handleUpload = async () => {
    if (!file || !user?.uid) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.uid);

    if (fieldMap && typeof fieldMap === 'object' && Object.keys(fieldMap).length > 0) {
      formData.append('field_map', JSON.stringify(fieldMap));
    }

    dispatch({ type: 'PREVIEW_START' });

    try {
      const response = await fetch(`${API_BASE_URL}/map-csv`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const json = await response.json();

      if (json.properties && Array.isArray(json.properties) && json.properties.length > 0) {
        dispatch({ type: 'PREVIEW_SUCCESS', payload: { properties: json.properties, fieldMap } });
      } else if (json.status === 'processing_initiated') {
        toast.info(json.message || intl.formatMessage({ id: "csvUpload.processingAsync" }));
        dispatch({ type: 'PREVIEW_ERROR' });
      } else {
        toast.error(json.error || intl.formatMessage({ id: "csvUpload.processEmptyOrNoData" }));
        dispatch({ type: 'PREVIEW_ERROR' });
      }
    } catch (error) {
      dispatch({ type: 'PREVIEW_ERROR' });
      toast.error(error.message || intl.formatMessage({ id: "csvUpload.networkError" }));
    }
  };

  const saveToFirestore = async (replace = false) => {
    if (!file || !user?.uid) {
      toast.error(intl.formatMessage({ id: "csvUpload.noFileSelected" }));
      return;
    }
    dispatch({ type: 'SAVE_START' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.uid);
    formData.append('replace', replace.toString());

    if (fieldMap && Object.keys(fieldMap).length > 0) {
      formData.append('field_map', JSON.stringify(fieldMap));
    }

    // Use `/upload-csv` to match the `firebase.json` rewrite configuration.
    const ASYNC_UPLOAD_URL = import.meta.env.VITE_CSV_PROCESSOR_ASYNC_URL || `${API_BASE_URL}/upload-csv-async`;

    try {
      const response = await fetch(ASYNC_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const json = await response.json();

      if (json.status === 'processing_initiated' && json.job_id) {
        dispatch({ type: 'SAVE_INITIATED', payload: json.job_id });
      } else {
        toast.error(json.error || intl.formatMessage({ id: 'csvUpload.asyncInitiationError' }));
        dispatch({ type: 'SAVE_ERROR' });
      }
    } catch (error) {
      console.error("Error initiating asynchronous CSV processing:", error);
      toast.error(error.message || intl.formatMessage({ id: 'csvUpload.networkError' }));
      dispatch({ type: 'SAVE_ERROR' });
    }
  };

  const handleDeleteAllProperties = async () => {
    dispatch({ type: 'TOGGLE_DELETE_CONFIRMATION' });
    dispatch({ type: 'DELETE_ALL_START' });

    const DELETE_ALL_URL = `${API_BASE_URL}/delete-all-properties`;

    try {
      if (!auth.currentUser) {
        toast.error(intl.formatMessage({ id: 'csvUpload.errorNoUser' }));
        dispatch({ type: 'DELETE_ALL_ERROR' });
        return;
      }
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(DELETE_ALL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ user_id: user.uid }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      toast.success(intl.formatMessage({ id: 'csvUpload.deleteAllSuccess' }));
      dispatch({ type: 'DELETE_ALL_COMPLETE' });
      // Removed setTimeout - UI update should happen immediately
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Error deleting all properties:", error);
      toast.error(error.message || intl.formatMessage({ id: 'csvUpload.deleteAllError' }));
      dispatch({ type: 'DELETE_ALL_ERROR' });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_DRAGGING', payload: true });
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_DRAGGING', payload: false });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_DRAGGING', payload: false });
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  return (
    <>
      <div
        className={`upload-container drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          id="csv-file"
          type="file"
          accept=".csv"
          onChange={async (e) => await processFile(e.target.files[0])}
          style={{ display: 'none' }}
          disabled={uploading || saving}
        />
        <label htmlFor="csv-file" className="custom-file-upload-label">
          {intl.formatMessage({ id: 'csvUpload.dragOrClick' })}{' '}
          <span className="file-upload-link">
            {intl.formatMessage({ id: 'properties.chooseFile' })}
          </span>
        </label>
        <button className="btn-primary" onClick={handleUpload} disabled={!file || uploading || saving || result.length > 0}>
          {intl.formatMessage({ id: 'csvUpload.button.upload' })}
        </button>
      </div>

      {file && (
        <div className="csv-file-feedback" style={{ marginTop: '0.5rem' }}>
          📎 {file.name}
        </div>
      )}

      {uploading && !saving && (
        <div className="progress-container">
          <div className="spinner"></div>
          <p className="progress-text">
            {intl.formatMessage({ id: 'csvUpload.generatingPreview', defaultMessage: 'Gerando pré-visualização...' })}
          </p>
        </div>
      )}

      {result.length > 0 && !saving && (
        <>
          <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
            {intl.formatMessage({ id: 'csvUpload.preview', defaultMessage: 'Pré-visualização dos imóveis transformados' })}
          </h4>
          <div className="save-options-box">
            <p>
              {hasExistingProperties
                ? intl.formatMessage({ id: 'csvUpload.saveOptionsSubtitle' })
                : intl.formatMessage({ id: 'csvUpload.saveOptionsSave' })}
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              {hasExistingProperties ? (
                <>
                  <button onClick={() => saveToFirestore(true)} className="btn-danger" disabled={saving}>
                    {intl.formatMessage({ id: 'csvUpload.replaceButton' })}
                  </button>
                  <button onClick={() => saveToFirestore(false)} className="btn-secondary" disabled={saving}>
                    {intl.formatMessage({ id: 'csvUpload.syncButton' })}
                  </button>
                </>
              ) : (
                <button onClick={() => saveToFirestore(false)} className="btn-primary" disabled={saving}>
                  {intl.formatMessage({ id: 'csvUpload.button.save' })}
                </button>
              )}
            </div>
          </div>

          <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
            {intl.formatMessage({ id: 'properties.registeredPropertiesTitle', defaultMessage: 'Imóveis Cadastrados' })}
          </h3>

          <ul style={{ marginTop: '1rem', listStyle: 'none', paddingLeft: 0 }}>
            {result.slice(0, 3).map((p, i) => (
              <li key={p.id || i} style={{ marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid #eee', borderRadius: '4px'}}>
                <strong>{p.tipo || 'Imóvel'}</strong> — {p.bairro || 'Bairro não informado'}, {p.cidade || 'Cidade não informada'} – R${(p.preco || p.valorVenda)?.toLocaleString() || 'Preço não informado'}
                {p.validationErrors && (
                  // Renderiza o div de erros SOMENTE se houver erros reais no array
                  p.validationErrors.length > 0 && (
                    <div style={{color: 'red', fontSize: '0.8em', marginTop: '0.3em'}}>
                      Erros: {p.validationErrors.join(', ')}
                    </div>
                  )
                )}
              </li>
            ))}
          </ul>
          {result.length > 3 && (
            <p>{intl.formatMessage({ id: 'csvUpload.previewLimited' }, { count: result.length - 3 })}</p>
          )}
        </>
      )}
      
      {/* --- DANGER ZONE MOVED AND CONSOLIDATED --- */}
      {hasExistingProperties && !saving && !uploading && (
        <div style={{ marginTop: '2rem', padding: '1rem', border: '1px solid #ffcccb', borderRadius: '4px', backgroundColor: '#fff5f5' }}>
          <h5 style={{ color: '#d9534f', marginTop: 0 }}>
            {intl.formatMessage({ id: 'csvUpload.dangerZoneTitle' })}
          </h5>
          <p className="section-subtitle">
            {intl.formatMessage({ id: 'csvUpload.deleteAllWarning' })}
          </p>
          <button onClick={() => dispatch({ type: 'TOGGLE_DELETE_CONFIRMATION'})} className="btn-danger" disabled={isDeletingAll}>
            {isDeletingAll ? intl.formatMessage({ id: 'csvUpload.deletingAllButton' }) : intl.formatMessage({ id: 'csvUpload.deleteAllButton' })}
          </button>
        </div>
      )}

      {saving && currentJobId && jobStatus ? ( 
        <div className="progress-container">
          <div className="spinner"></div>
          <p className="progress-text">
            {jobStatus.status === 'processing' && jobStatus.total_rows_to_process
              ? intl.formatMessage(
                  { id: 'csvUpload.jobStatus.processingDetailed' },
                  {
                    progress: jobStatus.progress || 0,
                    processed_rows: jobStatus.processed_rows || 0,
                    total_rows_to_process: jobStatus.total_rows_to_process,
                  }
                )
              : jobStatus.status === 'started'
              ? intl.formatMessage({ id: 'csvUpload.jobStatus.waiting', defaultMessage: 'Aguardando início do processamento...' })
              : intl.formatMessage({ id: 'csvUpload.initiatingSave', defaultMessage: 'Iniciando salvamento em segundo plano...' })
            }
          </p>
          {jobStatus.status === 'processing' && jobStatus.progress !== undefined && (
            <div style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginTop: '8px', height: '10px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${jobStatus.progress || 0}%`,
                  backgroundColor: '#00aeef', 
                  height: '100%',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease-in-out'
                }}
              />
            </div>
          )}
        </div>
      ) : saving ? ( 
        <div className="progress-container">
          <div className="spinner"></div>
          <p className="progress-text">
            {intl.formatMessage({ id: 'csvUpload.initiatingSave', defaultMessage: 'Iniciando salvamento em segundo plano...' })}
          </p>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => dispatch({ type: 'TOGGLE_DELETE_CONFIRMATION' })}
        onConfirm={handleDeleteAllProperties}
        title={intl.formatMessage({ id: 'csvUpload.confirmDeleteAllTitle' })}
        message={intl.formatMessage({ id: 'csvUpload.confirmDeleteAllMessage' })}
        confirmButtonText={intl.formatMessage({ id: 'csvUpload.confirmDeleteButton' })}
        cancelButtonText={intl.formatMessage({ id: 'general.cancel' })}
      />
    </>
  );
}
